import React from 'react';
import PropTypes from 'prop-types';
import {defineMessages} from 'react-intl';
import {ParentBox} from '../box-utils/ParentBox.react.js';
import BoxDecorator from '../box-utils/BoxDecorator.react';
import {viewWithDataGetPrimaryData} from '../box-utils/PrimaryData';
import PanelColumns from './system/PanelColumns.react';
import {safeJSONparse} from "../../../common/util/safeJSONparse";
import StatusIcon from './StatusIcon.react';
import {getLocalizedCountryName} from '../../locales';

const messages = defineMessages({
    vatOk: {id: 'scenario.screen.VatValidation.vatOk', 'defaultMessage': 'Valid EU VAT number'},
    vatFail: {id: 'scenario.screen.VatValidation.vatFail', 'defaultMessage': 'Invalid EU VAT number'},
    vatUnknow: {id: 'scenario.screen.VatValidation.vatUnknow', 'defaultMessage': 'Cannot verify VAT number at this moment.'},
	compnayName: {id: 'common.companyExample.name', 'defaultMessage': 'Company Ltd.'},
	compnayAddress: {id: 'common.companyExample.address', 'defaultMessage': 'Fox Road 789, W5 5YZ London'},
	countryCode: {id: 'common.companyExample.countryCode', 'defaultMessage': 'GB'},
});

export default class VatValidation extends ParentBox {
    static handlerName = "vat-validation";

    static propTypes = {
        intl: PropTypes.object.isRequired,
        isPreview: PropTypes.bool,
        viewWithData: PropTypes.object.isRequired,
    };

    getIcon() {
        return 'fa-building';
    }

    getServiceName() {
        if (this.props.viewWithData.view.entityViewDef) {
            return this.props.viewWithData.view.entityViewDef.title;
        } else {
            return '';
        }
    }

    getPreviewData() {
        return [{
            "not-stored": true,
            "channel": "EU VAT number validation",
            "com.dativery.model.entities.countries@countryCodeIso31661Alpha2": this.props.intl.formatMessage(messages.countryCode),
            "b7b3cc59-7f41-47ff-a63c-c193ad36f59b": null,
            "ga.euvat.numberValidation.entity@requestDate": null,
            "e7d4e3fa-a96a-4cc7-a845-4696faf95dc9": this.props.intl.formatMessage(messages.compnayAddress),
            "com.dativery.model.components.seatAddresses@saFullAddress": this.props.intl.formatMessage(messages.compnayAddress),
            "64e8449d-421c-41e3-ba00-46e1fec51b06": "---",
            "ga.euvat.numberValidation.entity@companyType": "---",
            "3c11e208-4c99-4d12-af4c-5bbaee20090b": this.props.intl.formatMessage(messages.compnayName),
            "com.dativery.model.entities.companies@name": this.props.intl.formatMessage(messages.compnayName),
            "c8c9fdf6-879a-4c62-b2cc-e0ca9f186eb4": true,
            "com.dativery.model.sharedFields.valid@valid": true,
            "ecbec7ae-ef03-4c4d-9981-98705ad51fb1": {
                "standard_rate": 21,
                "reduced_rate": 15,
                "reduced_rate_alt": 10,
                "super_reduced_rate": false,
                "parking_rate": false
            },
            "ga.euvat.numberValidation.entity@vatRates": {
                "standard_rate": 21,
                "reduced_rate": 15,
                "reduced_rate_alt": 10,
                "super_reduced_rate": false,
                "parking_rate": false
            },
            "ef8528eb-67dc-48cd-8dc2-901b53445dbc": "7654321",
            "com.dativery.model.entities.companies@euVatWithoutCountryCode": "7654321"
        }];
    }

    getCompany(values) {
        let companyName = values["com.dativery.model.entities.companies@name"];

        if (companyName) {
            return (
                <div>
                    {companyName}<br />
                    {this.getCompanyAddress(values)}
                </div>
            );
        }

        return null;
    }

    getCompanyAddress(values) {
        let companyAddress = values["com.dativery.model.components.seatAddresses@saFullAddress"];

        if (companyAddress) {
            return companyAddress.replace(/\n/g, ', ');
        }

		if (values["com.dativery.model.components.seatAddresses@saAddress1"]) {
            let addressArray = [values["com.dativery.model.components.seatAddresses@saAddress1"], values["com.dativery.model.components.seatAddresses@saAddress2"], values["com.dativery.model.components.seatAddresses@saAddress3"], values["com.dativery.model.components.seatAddresses@saAddress4"], values["com.dativery.model.components.seatAddresses@saAddress5"]];
            return addressArray.filter(item => !!item).join(", ");
        }

        return null;
    }

    static getVATCode(values) {
        let vatCode = values["com.dativery.model.entities.companies@euVatCode"];
        let countryCode = values["com.dativery.model.entities.countries@countryCodeIso31661Alpha2"];
        let vatCodeWithoutCountryCode = values["com.dativery.model.entities.companies@euVatWithoutCountryCode"];
        if (vatCode) {
            return vatCode;
        } else if (countryCode && vatCodeWithoutCountryCode) {
            return countryCode + vatCodeWithoutCountryCode;
        }
        return undefined;
    }

    renderStatus(values) {
        let valid = values["com.dativery.model.sharedFields.valid@valid"];

        if (valid === true) {
            return (<StatusIcon title={this.props.intl.formatMessage(messages.vatOk)} type={StatusIcon.OK}/>);
        }

		if (valid === false) {
            return (<StatusIcon title={this.props.intl.formatMessage(messages.vatFail)} type={StatusIcon.ERROR}/>);
        }

        return (<StatusIcon title={this.props.intl.formatMessage(messages.vatUnknow)} type={StatusIcon.HELP}/>);
    }

    getCountry(values) {
        let countryCode = values["com.dativery.model.entities.countries@countryCodeIso31661Alpha2"];

		return getLocalizedCountryName(countryCode);
    }

    renderInfo(values, idx) {
        let vatCode = VatValidation.getVATCode(values);

        return (
            <div key={idx}>
                <span><strong>{vatCode}</strong> {this.renderStatus(values)} {this.getCountry(values)}</span><br/>
                {this.getCompany(values)}
            </div>
        );
    }

	static cleanData(data) {
		return data.filter(values => VatValidation.getVATCode(values) !== undefined);
	}

	static isEmpty(viewWithData) {
		let data = viewWithDataGetPrimaryData(this.props.viewWithData);

		return VatValidation.cleanData(data).length === 0;
	}

    render() {
        let data = this.props.isPreview ? this.getPreviewData() : safeJSONparse(this.props.viewWithData.data);
		data = VatValidation.cleanData(data);

        return (
            <BoxDecorator className={this.handlerName} icon={this.getIcon()} isPreview={this.props.isPreview} padding title={this.getServiceName()}>
                <PanelColumns className="boxOneTimeFontSize">
                    {data.map((values, idx) => {
                        return this.renderInfo(values, idx);
                    })}
                </PanelColumns>
            </BoxDecorator>
        );
    }
}
